import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ExtractedTableJson } from '@frontend/api/src/parts/useGetExtractedTables';

import { getActiveFile } from 'modules/common/viewer/data/reducer';
import {
  buildExtractedTableAnnotations,
  SUGGESTION_ANNOTATION_COMMAND_TYPES,
  ExtractedTableMetadata,
  SuggestionAnnotationRaw,
} from 'modules/common/viewer/pdfViewer/annotationCommands';
import { suggestionAnnotations } from 'modules/common/viewer/pdfViewer/data/actions';
import { getSuggestionAnnotationStateById } from 'modules/common/viewer/pdfViewer/data/reducer';
import { MINIMUM_CONFIDENCE } from 'modules/extractedTables/constants';

import { RootState } from 'store/configureStore';

type UseAnnotationInteractionsType = {
  extractedTablesData?: ExtractedTableJson[];
  hasSelectedTable: boolean;
  setSelectedTableUrl: (value: string | null) => void;
  extractedTableKey?: string;
};

export const useAnnotationInteractions = ({
  extractedTablesData,
  hasSelectedTable,
  setSelectedTableUrl,
  extractedTableKey,
}: UseAnnotationInteractionsType): {
  tableAnnotations: SuggestionAnnotationRaw[];
} => {
  const activeFileUuid: string | null =
    useSelector(getActiveFile)?.uuid ?? null;

  const dispatch = useDispatch();

  const tableAnnotationsList = useMemo(() => {
    if (extractedTablesData) {
      // filter for tables with greater than a certain confidence threshold
      const useTables = extractedTablesData.filter(
        (table) => table.confidence && table.confidence >= MINIMUM_CONFIDENCE,
      );
      return buildExtractedTableAnnotations(useTables);
    } else {
      return [];
    }
  }, [extractedTablesData]);

  // use annotation state to determine if we have a selected (focused) table
  // if so, set the internal state variable
  const selectedAnnotation = useSelector((state: RootState) => {
    return tableAnnotationsList.find(({ id }) => {
      const annState = getSuggestionAnnotationStateById(state, id);
      return annState?.isFocused ?? false;
    });
  });

  useEffect(() => {
    if (selectedAnnotation) {
      const metadata = selectedAnnotation.metadata as ExtractedTableMetadata;
      const { tableJson } = metadata;
      setSelectedTableUrl(tableJson.authenticatedUrl);
    }
  }, [selectedAnnotation, setSelectedTableUrl]);

  // handle focusing and unfocusing
  const prevExtractedTableKeyRef = useRef<string | undefined>(
    extractedTableKey,
  );
  useEffect(() => {
    if (activeFileUuid && extractedTableKey) {
      if (hasSelectedTable) {
        dispatch(
          suggestionAnnotations.queueCommands({
            commands: [
              {
                type: SUGGESTION_ANNOTATION_COMMAND_TYPES.FOCUS,
                annotationIds: [extractedTableKey],
              },
            ],
          }),
        );
      } else {
        // ensure that the data is set to None if closing
        // NOTE: we need to call this here because it can be closed via means
        // other than deselection in the pdf viewer
        // NB: This never seems to actually be called, but in the interest of non-destructive changes, I will leave
        // it here until we have better coverage
        setSelectedTableUrl(null);
        dispatch(
          suggestionAnnotations.queueCommands({
            commands: [
              {
                type: SUGGESTION_ANNOTATION_COMMAND_TYPES.UNFOCUS,
                annotationIds: [extractedTableKey],
              },
            ],
          }),
        );
      }
    }

    // We need to unfocus the table annotation if we no longer have an extracted table key / selected table
    if (
      activeFileUuid &&
      !extractedTableKey &&
      prevExtractedTableKeyRef.current
    ) {
      dispatch(
        suggestionAnnotations.queueCommands({
          commands: [
            {
              type: SUGGESTION_ANNOTATION_COMMAND_TYPES.UNFOCUS,
              annotationIds: [prevExtractedTableKeyRef.current],
            },
          ],
        }),
      );
    }

    // Update the ref with the current value after all the logic
    prevExtractedTableKeyRef.current = extractedTableKey;
  }, [
    activeFileUuid,
    dispatch,
    hasSelectedTable,
    extractedTableKey,
    setSelectedTableUrl,
  ]);

  return { tableAnnotations: tableAnnotationsList };
};
